/** Default inbound postMessage host suffixes (host + all subdomains). */
export const DEFAULT_ALLOWED_MESSAGE_HOST_SUFFIXES = [
  "knoffice.tech",
  "converge.ai",
] as const;

/**
 * Returns whether `origin` is allowed by `allowed`.
 *
 * Each entry in `allowed` is either:
 * - a **host suffix** (e.g. `knoffice.tech`) — matches that host and `*.knoffice.tech`;
 * - a **full origin** (e.g. `https://creator.knoffice.tech`) — exact match;
 * - `'*'` — accept any origin (escape hatch).
 */
export function isAllowedMessageOrigin(
  origin: string,
  allowed: readonly string[],
): boolean {
  if (allowed.includes("*")) {
    return true;
  }
  if (allowed.length === 0) {
    return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(origin);
  } catch {
    return false;
  }

  const originLower = origin.toLowerCase();
  const hostLower = parsed.hostname.toLowerCase();

  for (const entry of allowed) {
    const trimmed = entry.trim();
    if (!trimmed) {
      continue;
    }

    if (trimmed.includes("://")) {
      if (originLower === trimmed.toLowerCase()) {
        return true;
      }
      continue;
    }

    const suffix = trimmed.toLowerCase().replace(/^\./, "");
    if (hostLower === suffix || hostLower.endsWith(`.${suffix}`)) {
      return true;
    }
  }

  return false;
}

/** Defaults plus any extra entries (deduped). `['*']` alone accepts any origin. */
export function mergeAllowedMessageOrigins(extra?: string[]): string[] {
  if (!extra?.length) {
    return [...DEFAULT_ALLOWED_MESSAGE_HOST_SUFFIXES];
  }
  if (extra.includes("*")) {
    return ["*"];
  }

  const seen = new Set<string>();
  const result: string[] = [];
  for (const entry of [...DEFAULT_ALLOWED_MESSAGE_HOST_SUFFIXES, ...extra]) {
    const trimmed = entry.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}
